import { useMemo, useState } from "react";
import { filterLocations } from "@/frontend/components/FilterV2/const/const";
import { LocationsSelector } from "@/frontend/components/filter/ui/LocationsSelector/LocationsSelector";
import styles from "./BasicFilters.module.scss";
import { DollarIcon } from "@/frontend/shared/assets/icons/DollarIcon";
import { Typography } from "@/frontend/shared/ui/Typography";
import { CaretDownIcon } from "@/frontend/shared/assets/icons";
import clsx from "clsx";
import { Checkbox } from "@/frontend/shared/ui/Checkbox";
import { v4 } from "uuid";
import type { ConditionRowFilter } from "@/frontend/components/FilterV2/model/types";
import { companyStatus } from "@/frontend/shared/const/const";

type TBasicFiltersProps = {
  filters: ConditionRowFilter[];
  setFilters: (filters: ConditionRowFilter[]) => void;
};

const { PrivateEquity, ...realCompanyStatus } = companyStatus;

export function BasicFilters({ filters, setFilters }: TBasicFiltersProps) {
  const [collapsedFinancialStage, setCollapsedFinancialStage] = useState(false);
  const [locationSearchText, setLocationSearchText] = useState("");

  const financialStageOptions = Object.keys(realCompanyStatus).map((l) => ({
    value: l.replace(/\s/g, ""),
    label: realCompanyStatus[l]
  }));

  const existentFinancialStages = useMemo(() => {
    return filters.filter((f) => f.type === "financialStage");
  }, [filters]);
  const existentLocations = useMemo(() => {
    return filters.filter((f) => f.type === "location" && !!f.value);
  }, [filters]);

  return (
    <>
      <LocationsSelector
        selectedLocation={locationSearchText}
        locationOptions={filterLocations}
        setSelectedLocation={setLocationSearchText}
        locationsTags={existentLocations.map((v) => v.value as string) || []}
        setLocationsTags={(prev) => {
          if (typeof prev === "function") {
            const newTags = prev({ locationsTags: existentLocations.map((v) => v.value as string) || [] });
            const conditions: ConditionRowFilter[] = newTags.locationsTags.map((v) => ({
              id: v4(),
              type: "location",
              value: v,
              operand: "is",
              isOr: true
            }));

            setFilters([...filters.filter((f) => f.type !== "location"), ...conditions]);
          }
        }}
        placeholder="Enter location"
      />

      <div className={styles.block}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>
            <DollarIcon />
            <Typography variant="label-4" color="greyscale900">
              Financial stage
            </Typography>
          </div>
          <CaretDownIcon
            onClick={() => setCollapsedFinancialStage(!collapsedFinancialStage)}
            className={clsx(styles.caretDownIcon, collapsedFinancialStage && styles.rotatedIcon)}
          />
        </div>
        {!collapsedFinancialStage && (
          <div className={styles.subBlockCheckboxes}>
            {financialStageOptions?.map(({ value, label }) => {
              const isChecked = existentFinancialStages.some((f) => f.value === value);
              return (
                <div className={styles.checkboxContainer} key={value}>
                  <Checkbox
                    labelClassName={styles.checkboxLabel}
                    label={label}
                    value={isChecked}
                    onChange={() => {
                      if (!isChecked) {
                        setFilters([
                          ...filters,
                          {
                            id: v4(),
                            type: "financialStage",
                            value,
                            operand: "is",
                            isOr: true
                          }
                        ]);
                      } else {
                        const restFilters: ConditionRowFilter[] = [];
                        const financialStageFilters: ConditionRowFilter[] = [];
                        filters.forEach((f) => {
                          if (f.type === "financialStage" && f.value === value) {
                            financialStageFilters.push(f);
                          } else {
                            restFilters.push(f);
                          }
                        });
                        setFilters([
                          ...restFilters,
                          ...financialStageFilters.filter((f) => f.type === "financialStage" && f.value !== value)
                        ]);
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
